const context = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;

class PollsClient {
    constructor() {
        this.polls = [];
    }

    start() {}

    _fetchPolls() {
        const data = {
            viewed_uuid: context.viewedUUID,
            ...this._getLoadAjaxData(),
        };
        const headers = this.getAjaxHeaders();

        return $.ajax({
            url: context.pollsUrl,
            headers,
            data,
        });
    }

    _getLoadAjaxData() {
        const searchParams = new URLSearchParams(window.location.search);
        const forcePollId = searchParams.get('force_poll_id');
        if (forcePollId) {
            return { force_poll_id: forcePollId };
        }
        return {};
    }

    getAjaxHeaders() {
        return { 'X-CSRFToken': window.csrftoken() || context.csrftoken, Accept: 'application/json' };
    }

    trackEvent(events) {
        const headers = this.getAjaxHeaders();
        const data = JSON.stringify({
            timestamp: new Date().toISOString(),
            events,
        });

        $.ajax({
            url: context.pollsTrackingUrl,
            dataType: 'json',
            contentType: 'application/json; charset=utf-8',
            method: 'POST',
            data,
            headers,
        });
    }
}

export default class BrightcovePollsClient extends PollsClient {
    constructor(player) {
        super();
        this._player = player;
        this._player.el_.player.options_ = {
            ...this._player.el_.player.options_,
            'data-interactivity-user-id': context.userId,
        };
        this.classChangeCallback = this.classChangeCallback.bind(this);
        this.observer = new MutationObserver(this.classChangeCallback);
        this.pollsStateData = {};
        this._handleEvents();
    }

    /**
     * - Whenever a poll question is submitted, this function is called.
     * - The function will store the answer in the pollsStateData object. The
     *   logic for storing the answer is to loop through the questions and store
     *   the answer in the first question that has not been answered yet.
     *   This will work well as we do not have multiple choice questions and
     *   there is no back button to change the answer.
     * - After storing the answer, check if all questions have been answered.
     *   If all questions have been answered, the poll will be marked as
     *   submitted and the event will be tracked.
     *
     * @param annotationId
     * @param answerText
     */
    pollQuestionSubmitted(annotationId, answerText) {
        let numberOfAnsweredQuestions = 0;
        for (let i = 0; i < this.pollsStateData[annotationId].questions.length; i += 1) {
            const question = this.pollsStateData[annotationId].questions[i];
            numberOfAnsweredQuestions += 1;
            if (question.selected_choices === null) {
                question.selected_choices = [answerText];
                break;
            }
        }

        if (numberOfAnsweredQuestions < this.pollsStateData[annotationId].questions.length) {
            return;
        }

        this.pollsStateData[annotationId].submitted = true;
        this.pollsStateData[annotationId].active = false;
        const pollViewedId = this.brightcoveAnnotationIdToPoll[annotationId].poll_viewed_id;
        this.trackEvent([
            {
                eventName: 'submit',
                pollViewedId,
                data: { answer: this.pollsStateData[annotationId].questions },
            },
        ]);
    }

    pollDismissed(annotationId) {
        this.pollsStateData[annotationId].manuallyDismissed = true;
        this.pollsStateData[annotationId].active = false;
        const pollViewedId = this.brightcoveAnnotationIdToPoll[annotationId].poll_viewed_id;
        this.trackEvent([{ eventName: 'manual_dismiss', pollViewedId }]);
    }

    pollShown(annotationId) {
        this.pollsStateData[annotationId].active = true;
        const pollViewedId = this.brightcoveAnnotationIdToPoll[annotationId].poll_viewed_id;
        this.trackEvent([{ eventName: 'shown', pollViewedId }]);
    }

    pollAutoDismissed(annotationId) {
        this.pollsStateData[annotationId].active = false;
        const pollViewedId = this.brightcoveAnnotationIdToPoll[annotationId].poll_viewed_id;
        this.trackEvent([{ eventName: 'auto_dismiss', pollViewedId }]);
    }

    pollAttributeChanged(annotationId, active) {
        // Add a delay to ensure manual submit/dismiss is captured
        setTimeout(() => {
            const poll = this.pollsStateData[annotationId];

            if (poll.active === active) {
                // No change in state
                return;
            }

            if (active) {
                this.pollShown(annotationId);
            } else if (!poll.submitted && !poll.manuallyDismissed) {
                // The poll is auto-dismissed only if it was not submitted or manually dismissed
                this.pollAutoDismissed(annotationId);
            }
        }, 100);
    }

    start() {
        this._fetchPolls().done((data) => {
            this.polls = data;
            this.brightcoveAnnotationIdToPoll = {};
            for (const poll of this.polls) {
                this.brightcoveAnnotationIdToPoll[poll.brightcove_annotation_id] = poll;
                this.pollsStateData[poll.brightcove_annotation_id] = {
                    active: false,
                    submitted: false,
                    manuallyDismissed: false,
                    questions: [],
                };
                for (const question of poll.questions) {
                    this.pollsStateData[poll.brightcove_annotation_id].questions.push({
                        question: question.question_text,
                        selected_choices: null,
                    });
                }
            }
            this._waitForInteractivityPollsToLoad();
        });
    }

    _waitForInteractivityPollsToLoad() {
        /*
            Handle automatic poll form submission
            when an answer is selected
        */

        let interactivityPollsLoaded = false;
        const interactivityInterval = setInterval(() => {
            this._enableTargetedPolls();
            const interactivityPolls = Array.from(document.querySelectorAll('.popcorn-quiz'));
            interactivityPollsLoaded = interactivityPolls.length;
            if (interactivityPollsLoaded) {
                clearInterval(interactivityInterval);
                this._pollsLoaded();
                this._setSubmitOnSelect(interactivityPolls);
            }
        }, 500);
    }

    _pollsLoaded() {
        const brightcovePollsData = this._player.interactivityViewerInstance.getData();
        const events = [];
        for (const poll of this.polls) {
            if (brightcovePollsData.annotationIds[poll.brightcove_annotation_id]) {
                events.push({ eventName: 'loaded', pollViewedId: poll.poll_viewed_id });
            }
        }
        if (events.length) {
            this.trackEvent(events);
        }
    }

    _setSubmitOnSelect(interactivityPolls) {
        interactivityPolls.forEach((poll) => {
            this.observer.observe(poll, { attributes: true });
            const pollAnswerInputs = Array.from(poll.querySelectorAll('.answer input[type="radio"]'));
            const submitBtn = poll.querySelector('.btn.submit-answers');
            const cancelBtn = poll.querySelector('.btn.btn-large.skip');
            const brightcoveAnnotationId = poll.dataset.hapyakId;

            if (poll.classList.contains('active')) {
                this.pollShown(brightcoveAnnotationId);
            }

            cancelBtn.addEventListener('click', () => {
                this.pollDismissed(brightcoveAnnotationId);
            });

            pollAnswerInputs.forEach((answer) => {
                const answerText = answer.parentElement.querySelector('.label-text').innerText;
                answer.addEventListener('change', () => {
                    submitBtn.click();
                    this.pollQuestionSubmitted(brightcoveAnnotationId, answerText);
                });
            });
        });
    }

    _enableTargetedPolls() {
        if (!this._player.interactivityViewerInstance) {
            return;
        }
        for (const poll of this.polls) {
            Object.keys(poll.brightcove_display_rules).forEach((rule) => {
                const value = poll.brightcove_display_rules[rule];
                this._player.interactivityViewerInstance.setData(rule, value);
            });
        }
    }

    classChangeCallback(mutationsList) {
        for (const mutation of mutationsList) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                const brightcoveAnnotationId = mutation.target.dataset.hapyakId;
                let active = false;
                active = $(mutation.target).hasClass('active');
                this.pollAttributeChanged(brightcoveAnnotationId, active);
            }
        }
    }

    trackEventForActivePolls(eventName) {
        const events = [];
        for (const poll of this.polls) {
            if (this.pollsStateData[poll.brightcove_annotation_id].active) {
                events.push({ eventName, pollViewedId: poll.poll_viewed_id });
            }
        }
        if (events.length) {
            this.trackEvent(events);
        }
    }

    _handleEvents() {
        this._unloadEvents();
        this._focusOutEvents();
        this._focusInEvents();
    }

    _focusOutEvents() {
        window.addEventListener('blur', () => {
            this.trackEventForActivePolls('window_focus_lost');
        });
    }

    _focusInEvents() {
        window.addEventListener('focus', () => {
            this.trackEventForActivePolls('window_focus_retrieved');
        });
    }

    _unloadEvents() {
        // Page close
        window.addEventListener('beforeunload', () => {
            this.trackEventForActivePolls('page_close_dismiss');
        });
    }
}
